// src/components/LanguageSwitcher.tsx
import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import styled, { keyframes } from "styled-components"
import { useTranslation } from "react-i18next"
import { getLocalizedPath, getRouteKeyFromPath } from "./routeUtils"

type LanguageKey = "en" | "fr" | "es"
const languages: { code: LanguageKey; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
]

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
`

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;

  padding: 0.8rem 2rem;

  &:hover {
  }
`
const SelectedButton = styled.button`
  background: transparent;
  border: 1.5px solid #e0e0e0;

  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    border: 2.5px solid ${(p) => p.theme.primary};

    color: ${(p) => p.theme.primary};
    padding: 10px 20px;
    font-weight: 600;
    font-size: 1rem;
  }
`
const OptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: ${(p) => p.theme.primary}70;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  animation: ${fadeIn} 0.2s ease-out;
  z-index: 100;

  @media (max-width: 768px) {
    left: 2rem;
  }
`
const OptionItem = styled.li`
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  &:hover {
    background: ${(p) => p.theme.primary}50;
  }
  /* 
  span {
    color: white !important;
  } */
`

interface Props {
  onSwitch?: () => void
}
const LanguageSwitcher: React.FC<Props> = ({ onSwitch }) => {
  const { i18n } = useTranslation()
  const currentLang = i18n.resolvedLanguage as LanguageKey
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [])

  const switchTo = (newLang: LanguageKey) => {
    if (newLang === currentLang) return setIsOpen(false)
    i18n.changeLanguage(newLang).then(() => {
      document.documentElement.lang = newLang
      const key = getRouteKeyFromPath(location.pathname, currentLang) || "home"
      const to = getLocalizedPath(key, newLang)
      navigate(to, { replace: true })
      onSwitch?.()
    })
    setIsOpen(false)
  }

  const curr = languages.find((l) => l.code === currentLang) ?? languages[0]
  return (
    <DropdownWrapper ref={ref}>
      <SelectedButton onClick={() => setIsOpen((o) => !o)}>
        <span>{curr.flag}</span>
        <span>{curr.label}</span>
      </SelectedButton>
      {isOpen && (
        <OptionsList>
          {languages.map(({ code, label, flag }) => (
            <OptionItem key={code} onClick={() => switchTo(code)}>
              <span>{flag}</span>
              <span>{label}</span>
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </DropdownWrapper>
  )
}

export default LanguageSwitcher
